// تقويمٌ منسدل يُرسَم في الصفحة (قرار المالك: «عاوز خانات التاريخ في البرنامج تبقى
// زي كده، وأصغر حاجة بسيطة» — ومعها صورةُ تقويمٍ بشبكة أيامٍ وسهمَي شهر).
//
// **ولماذا لا تقويم المتصفّح**: `input[type=date]` يفتح تقويم نظام التشغيل — شكله
// يختلف بين ويندوز وأندرويد و«كروم» و«سفاري»، ولغته لغةُ النظام لا لغة البرنامج،
// وحجمه ليس بيدنا. فبرنامجٌ عربيٌّ كلُّه بخطٍّ واحد ومقاسٍ واحد كان يُفتح فيه صندوقٌ
// إنجليزيٌّ غريبٌ عنه. وهذا مرسومٌ هنا: نفس الخطّ ونفس الألوان ونفس المقاس.
//
// **والأرقام والأيام لاتينية عمدًا** (Su Mo Tu…): هي ما في الصورة التي أقرّها المالك،
// وهي كذلك ما تعوّدته العين في كل موقع حجزٍ يتعامل معه الفريق يوميًا. والعنوان وحده
// بالعربية لأنه يُقرأ لا يُمسح بالعين.
//
// ولا اعتمادية جديدة: حسابُ أيام الشهر بضع أسطر، ومكتبةُ تقويمٍ كاملة لأجلها إسراف.
import { useEffect, useMemo, useRef, useState } from 'react';

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

/** `YYYY-MM` — صيغة الشهر كما يقبلها الخادم وكل شاشات المرتبات والتسويات. */
const ym = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, '0')}`;

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** خلايا الشبكة: أيامُ الشهر ومعها ذيلُ الشهر السابق ورأسُ التالي — 6 صفوف ثابتة. */
function cellsOf(year: number, month: number) {
  const first = new Date(year, month, 1);
  const lead = first.getDay();                       // الأحد = 0، كما في صفّ الرؤوس
  const start = new Date(year, month, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate(), outside: d.getMonth() !== month };
  });
}

export interface CalendarProps {
  /** التاريخ المختار بصيغة ISO، أو '' حين لا اختيار. */
  value: string;
  onPick: (isoDate: string) => void;
  onClose: () => void;
  min?: string;
  max?: string;
  /**
   * `month`: **شهرٌ وسنة بلا يوم** — نصف شاشات النظام تسأل عن شهرٍ لا عن يوم
   * (المرتبات، المصاريف، التسويات، تصدير الشهر). وكانت تستعمل `input[type=month]`
   * فيفتح صندوق المتصفّح بأسماء شهورٍ إنجليزية داخل شاشةٍ عربية (قرار المالك: «أي
   * عنصر إدخال تاريخ خلّيه زي اللي موجودة في حجز جديد»). فتُفتح هنا شبكةُ الشهور
   * مباشرةً، ويُعاد `YYYY-MM`.
   */
  mode?: 'day' | 'month';
}

/**
 * لوحة التقويم نفسها. تُغلق بـEsc وبالنقر خارجها، ويبدأ عرضها على شهر المختار —
 * أو شهر اليوم إن لم يُختَر شيء، فمن فتحها يرى ما يبحث عنه لا يناير 1970.
 */
export function Calendar({ value, onPick, onClose, min, max, mode = 'day' }: CalendarProps) {
  const today = new Date();
  const monthMode = mode === 'month';
  const sel0 = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  // في وضع الشهر القيمة `YYYY-MM` لا `YYYY-MM-DD`
  const sel = monthMode ? (/^\d{4}-\d{2}$/.test(value) ? value : null) : sel0;
  const [year, setYear] = useState(() => (sel ? Number(sel.slice(0, 4)) : today.getFullYear()));
  const [month, setMonth] = useState(() => (sel ? Number(sel.slice(5, 7)) - 1 : today.getMonth()));
  const box = useRef<HTMLDivElement>(null);
  /**
   * **الحافّة** (قرار المالك 01/10/2026: «لما بضغط على الـcalendar في قيّمني جي على شمال الشاشة ومش بيظهر»):
   * التقويم كان يمتدّ من بداية حقله يسارًا 17rem بلا نظرٍ إلى مكانه، فحقلٌ على يسار الشاشة يُخرجه منها.
   * يُقاس مكان الحقل مرّةً عند الفتح: قريبٌ من اليسار ⇒ يُلصق بيساره، وإلّا بيمينه كما كان.
   */
  const [edge, setEdge] = useState('right-0');
  useEffect(() => {
    const parent = box.current?.parentElement;
    if (!parent) return;
    const r = parent.getBoundingClientRect();
    setEdge(r.left < 17 * 16 + 8 ? 'left-0' : 'right-0');
  }, []);

  // الإغلاق بالنقر خارجها أو بـEsc — ولا شيء ثالث يغلقها بالمصادفة
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    // يعلّم الحدث فلا تُغلق النافذة التي فيها مع التقويم (02/10/2026)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const cells = useMemo(() => cellsOf(year, month), [year, month]);
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const step = (by: number) => {
    const d = new Date(year, month + by, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };

  /**
   * **العنوان بابان: الشهر يفتح الشهور، والسنة تفتح السنين** (قرار المالك: «لما أضغط
   * على 2026 يجيبله السنين يختار منها» ثم «ولو ضغط على الشهر كمان يختار الشهور علطول»).
   *
   * والبديل كان النقر على السهم مرّةً لكل شهر — ومن يسجّل جواز سفرٍ لمولودٍ سنة 1978
   * كان أمامه خمسمائة نقرة. والشبكتان تُقلَّبان بالسهمين نفسيهما، فلا زرَّ يُتعلَّم.
   */
  const [view, setView] = useState<'days' | 'months' | 'years'>(monthMode ? 'months' : 'days');
  const [yearPage, setYearPage] = useState(() => Math.floor(year / 12) * 12);
  const years = useMemo(
    () => Array.from({ length: 12 }, (_, i) => yearPage + i), [yearPage]);

  const back = () => (view === 'years' ? setYearPage(p => p - 12)
    : view === 'months' ? setYear(y => y - 1) : step(-1));
  const fwd = () => (view === 'years' ? setYearPage(p => p + 12)
    : view === 'months' ? setYear(y => y + 1) : step(1));
  const backLabel = view === 'years' ? 'سنوات أسبق' : view === 'months' ? 'السنة السابقة' : 'الشهر السابق';
  const fwdLabel = view === 'years' ? 'سنوات أحدث' : view === 'months' ? 'السنة التالية' : 'الشهر التالي';

  /** زرُّ خليةٍ في شبكة الشهور أو السنين — نفس المظهر في الاثنتين. */
  const gridBtn = (on: boolean, hint: boolean) => `h-9 rounded-lg tabular-nums transition-colors ${
    on ? 'bg-brand-600 font-bold text-white'
      : hint ? 'bg-brand-50 font-semibold text-brand-700 hover:bg-brand-100'
        : 'text-ink-800 hover:bg-slate-100'}`;

  return (
    <div ref={box} role="dialog" aria-label="التقويم" dir="ltr"
      className={`absolute z-30 mt-1 w-[17rem] rounded-xl border border-slate-200 bg-white p-2.5 shadow-lg ${edge}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <button type="button" aria-label={backLabel} onClick={back}
          className="rounded-lg px-2 py-1 text-ink-500 hover:bg-slate-100 hover:text-ink-900">‹</button>

        {view === 'years' ? (
          <span className="px-2 py-1 text-sm font-semibold text-ink-800 tabular-nums">
            {years[0]} — {years[11]}
          </span>
        ) : (
          <span className="flex items-center gap-1">
            {/* بابان منفصلان: كلٌّ يفتح شبكته — والنقرة الثانية ترجع لشبكة الأيام.
                وفي وضع الشهر لا بابَ للشهور: الشبكة المعروضة هي الشهور نفسها. */}
            {!monthMode && (
            <button type="button" aria-label="اختيار الشهر" aria-expanded={view === 'months'}
              onClick={() => setView(v => (v === 'months' ? 'days' : 'months'))}
              className="rounded-lg px-2 py-1 text-sm font-semibold text-ink-800 hover:bg-slate-100">
              {MONTHS_AR[month]}
            </button>
            )}
            <button type="button" aria-label="اختيار السنة"
              onClick={() => { setYearPage(Math.floor(year / 12) * 12); setView('years'); }}
              className="rounded-lg px-2 py-1 text-sm font-semibold text-ink-800 tabular-nums hover:bg-slate-100">
              {year}
            </button>
          </span>
        )}

        <button type="button" aria-label={fwdLabel} onClick={fwd}
          className="rounded-lg px-2 py-1 text-ink-500 hover:bg-slate-100 hover:text-ink-900">›</button>
      </div>

      {view === 'years' ? (
        <div className="grid grid-cols-3 gap-1 py-1 text-center text-[13px]">
          {years.map(y => (
            <button key={y} type="button"
              // بعد السنة تُفتح الشهور: من غيّر سنته يغيّر شهره غالبًا
              onClick={() => { setYear(y); setView('months'); }}
              className={gridBtn(y === year, y === today.getFullYear())}>
              {y}
            </button>
          ))}
        </div>
      ) : view === 'months' ? (
        <div className="grid grid-cols-3 gap-1 py-1 text-center text-[13px]" dir="rtl">
          {MONTHS_AR.map((ar, i) => {
            const v = ym(year, i);
            // في وضع الشهر: المدى يُقارَن `YYYY-MM` بنفسه، والمعطَّل يُعرض ولا يُختار
            const blocked = monthMode
              && ((min !== undefined && min !== '' && v < min.slice(0, 7))
                || (max !== undefined && max !== '' && v > max.slice(0, 7)));
            return (
              <button key={ar} type="button" disabled={blocked || undefined}
                onClick={() => { if (monthMode) { onPick(v); onClose(); } else { setMonth(i); setView('days'); } }}
                className={blocked ? 'h-9 cursor-not-allowed rounded-lg tabular-nums text-ink-300'
                  : gridBtn(monthMode ? v === sel : i === month,
                    i === today.getMonth() && year === today.getFullYear())}>
                {ar}
              </button>
            );
          })}
        </div>
      ) : (
      <>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] font-semibold text-ink-500">
        {DOW.map(d => <span key={d} className="py-1">{d}</span>)}
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[13px]">
        {cells.map(c => {
          const v = iso(c.y, c.m, c.d);
          const picked = v === sel;
          // خارج المدى المسموح: يُعرض ولا يُختار — إخفاؤه يربك من يعدّ الأيام بعينه
          const blocked = (min !== undefined && min !== '' && v < min)
            || (max !== undefined && max !== '' && v > max);
          return (
            <button key={v} type="button" disabled={blocked}
              onClick={() => { onPick(v); onClose(); }}
              className={`h-7 rounded-lg tabular-nums transition-colors ${
                picked ? 'bg-brand-600 font-bold text-white'
                  : blocked ? 'cursor-not-allowed text-ink-300'
                    : c.outside ? 'text-ink-400 hover:bg-slate-100'
                      : v === todayIso ? 'bg-brand-50 font-semibold text-brand-700 hover:bg-brand-100'
                        : 'text-ink-800 hover:bg-slate-100'}`}>
              {c.d}
            </button>
          );
        })}
      </div>
      </>
      )}

      <div className="mt-1.5 flex justify-between border-t border-slate-100 pt-1.5" dir="rtl">
        <button type="button" className="rounded-lg px-2 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-50"
          onClick={() => { onPick(monthMode ? ym(today.getFullYear(), today.getMonth()) : todayIso); onClose(); }}>
          {monthMode ? 'الشهر ده' : 'النهارده'}
        </button>
        <button type="button" className="rounded-lg px-2 py-1 text-xs text-ink-500 hover:bg-slate-100"
          onClick={() => { onPick(''); onClose(); }}>مسح</button>
      </div>
    </div>
  );
}
